import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../app.service';
import { environment } from '../../../../environments/environment';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SettingsOpsService } from '../settings-ops/settings-ops.service';
import { SwitchUserModalComponent } from './switch-user-modal/switch-user-modal.component';
import { CreateSettingsModalComponent } from './create-settings-modal/create-settings-modal.component';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

export type SettingsTab = 'configSettings' | 'appconfigSettings' | 'clientconfigSettings';

@Component({
    selector: 'gtw-system-settings',
    templateUrl: './system-settings.component.html',
    styleUrls: ['./system-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SystemSettingsComponent implements OnInit, OnDestroy {
  tab: SettingsTab = 'configSettings';
  loading = false;
  sysHover = false;
  appHover = false;
  clientHover = false;
  clients: any[] = [];
  selectedAppClient: any = null;
  selectedClient: any = null;
  channelList: any[] = [];
  selectedChannel: any = null;
  searchConfig = '';
  searchAppConfig = '';
  searchClientConfig = '';
  showConfirmReset = false;
  confirmResetConfig: ConfirmationModalConfig = {
    title: 'Reset Server Cache',
    message: 'Are you sure you want to reset the servers config settings cache?',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  serverSettings: Record<SettingsTab, any[]> = {
    configSettings: [],
    appconfigSettings: [],
    clientconfigSettings: [],
  };

  private resetServerSettings: Record<SettingsTab, any[]> = {
    configSettings: [],
    appconfigSettings: [],
    clientconfigSettings: [],
  };

  private destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private settingsOpsService: SettingsOpsService,
    private gtwSnackbarService: GtwSnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  private get baseUrl(): string {
    return environment.base_url;
  }

  private get adminApiUrl(): string {
    return environment.admin_api_url;
  }

  ngOnInit(): void {
    this.initClientData();
    this.loadSettings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initClientData(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.clients = userSettings.user.clients || [];
      const currentClientKey = userSettings.user.client?.client_key ?? userSettings.user.client_key;
      this.selectedAppClient = this.clients.find((c: any) => String(c.client_key) === String(currentClientKey)) ?? this.clients[0] ?? null;
      this.selectedClient = this.selectedAppClient;
    }
  }

  setTab(tab: SettingsTab): void {
    this.tab = tab;
    this.cdr.detectChanges();
  }

  loadSettings(): void {
    if (!this.selectedClient) return;
    this.loading = true;
    this.cdr.detectChanges();

    this.settingsOpsService.getAppKeys(this.baseUrl, String(this.selectedClient.client_key ?? ''))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.channelList = data?.channel?.p_cursor || [];
          this.selectedChannel = this.channelList[0] || null;
          this.loadConfigAndClientSettings();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Unable to load app channels', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadConfigAndClientSettings(): void {
    this.settingsOpsService.getConfigSettings(this.baseUrl, this.selectedChannel?.channel_key || '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cfg: any) => {
          this.serverSettings.configSettings = this.decorateRows(cfg?.configSettings || []);
          this.serverSettings.clientconfigSettings = this.decorateRows(cfg?.clientconfigSettings || []);
          this.resetServerSettings.configSettings = JSON.parse(JSON.stringify(this.serverSettings.configSettings));
          this.resetServerSettings.clientconfigSettings = JSON.parse(JSON.stringify(this.serverSettings.clientconfigSettings));
          this.loadAppSettings();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Unable to load system settings', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  loadAppSettings(): void {
    if (!this.selectedAppClient || !this.selectedChannel) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();

    this.settingsOpsService
      .loadAppSettings(
        this.baseUrl,
        String(this.selectedAppClient.client_key ?? ''),
        String(this.selectedAppClient.client_datasource ?? ''),
        String(this.selectedChannel.channel_key ?? '')
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.serverSettings.appconfigSettings = this.decorateRows(data?.appconfigSettings || []);
          this.resetServerSettings.appconfigSettings = JSON.parse(JSON.stringify(this.serverSettings.appconfigSettings));
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Unable to load app settings', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  loadClientSettings(): void {
    if (!this.selectedClient) return;
    this.loading = true;
    this.cdr.detectChanges();

    this.settingsOpsService
      .loadClientSettings(
        this.baseUrl,
        String(this.selectedClient.client_key ?? ''),
        String(this.selectedClient.client_datasource ?? '')
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.serverSettings.clientconfigSettings = this.decorateRows(data?.clientconfigSettings || []);
          this.resetServerSettings.clientconfigSettings = JSON.parse(JSON.stringify(this.serverSettings.clientconfigSettings));
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Unable to load client settings', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onAppClientChange(): void {
    this.settingsOpsService.getAppKeys(this.baseUrl, String(this.selectedAppClient?.client_key ?? ''))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.channelList = data?.channel?.p_cursor || [];
          this.selectedChannel = this.channelList[0] || null;
          this.cdr.detectChanges();
        },
        error: () => {
          this.gtwSnackbarService.openSnackBar('Unable to load channels', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  toggleRow(row: any): void {
    row.toggle = !row.toggle;
    this.cdr.detectChanges();
  }

  onValueChange(row: any): void {
    row.enableActions = true;
    this.cdr.detectChanges();
  }

  saveRow(row: any, type: SettingsTab): void {
    row.loading = true;
    this.cdr.detectChanges();

    const payload: any = {
      key: row.NAME,
      value: row.VALUE,
      clientKey: this.selectedClient?.client_key ?? this.selectedAppClient?.client_key ?? '',
      clientDatasource: this.selectedClient?.client_datasource ?? this.selectedAppClient?.client_datasource ?? '',
      appKey: this.selectedChannel?.channel_key ?? '',
    };

    const req =
      type === 'appconfigSettings'
        ? this.settingsOpsService.saveAppSetting(this.baseUrl, payload)
        : type === 'clientconfigSettings'
          ? this.settingsOpsService.saveClientSetting(this.baseUrl, payload)
          : this.settingsOpsService.saveSystemSetting(this.baseUrl, payload);

    req.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        row.loading = false;
        if (String(data?.callSuccess) === '1') {
          row.enableActions = false;
          this.gtwSnackbarService.openSnackBar('Setting saved successfully.', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.p_error_out || 'Save failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        row.loading = false;
        this.gtwSnackbarService.openSnackBar('Error saving setting', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  resetRow(row: any, type: SettingsTab): void {
    const backup = (this.resetServerSettings[type] || []).find((x: any) => x.CONFIG_KEY === row.CONFIG_KEY);
    if (backup) {
      row.VALUE = backup.VALUE;
      row.enableActions = false;
      this.cdr.detectChanges();
    }
  }

  deleteRow(row: any, type: SettingsTab): void {
    row.loadingDeleteSettings = true;
    this.cdr.detectChanges();

    const req =
      type === 'appconfigSettings'
        ? this.settingsOpsService.deleteAppSetting(this.baseUrl, {
            key: row.NAME,
            clientKey: this.selectedAppClient?.client_key ?? '',
            appKey: this.selectedChannel?.channel_key ?? '',
            clientDatasource: this.selectedAppClient?.client_datasource ?? '',
          })
        : type === 'clientconfigSettings'
          ? this.settingsOpsService.deleteClientSetting(this.baseUrl, {
              key: row.NAME,
              clientKey: this.selectedClient?.client_key ?? '',
              clientDatasource: this.selectedClient?.client_datasource ?? '',
            })
          : this.settingsOpsService.deleteSystemSetting(this.baseUrl, row.NAME);

    req.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        row.loadingDeleteSettings = false;
        if (String(data?.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('Setting deleted successfully.', 'gtw-snackbar--green');
          this.refreshCurrentTab();
        } else {
          this.gtwSnackbarService.openSnackBar(data?.p_error_out || 'Delete failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        row.loadingDeleteSettings = false;
        this.gtwSnackbarService.openSnackBar('Error deleting setting', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  confirmResetServerCache(): void {
    this.showConfirmReset = true;
    this.cdr.detectChanges();
  }

  onConfirmResetYes(): void {
    this.showConfirmReset = false;
    const configMap: Record<SettingsTab, string> = {
      configSettings: 'coreConfig',
      appconfigSettings: 'appConfig',
      clientconfigSettings: 'clientConfig',
    };
    const payload = {
      clientKey: this.selectedClient?.client_key ?? '',
      clientDatasource: this.selectedClient?.client_datasource ?? '',
      config: configMap[this.tab],
      appKey: this.tab === 'appconfigSettings' ? (this.selectedChannel?.channel_key ?? '') : '',
    };
    this.loading = true;
    this.cdr.detectChanges();

    this.settingsOpsService.resetCache(this.adminApiUrl, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.loading = false;
          if (data?.ajaxSuccess) {
            this.gtwSnackbarService.openSnackBar('Server config cache was reset', 'gtw-snackbar--green');
            this.loadSettings();
          } else {
            this.gtwSnackbarService.openSnackBar('Error: cache was not reset. You might not have the correct permissions.', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar('Error resetting cache', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onConfirmResetNo(): void {
    this.showConfirmReset = false;
    this.cdr.detectChanges();
  }

  openSwitchUserModal(): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '550px';
    config.disableClose = false;
    config.data = {
      baseUrl: this.baseUrl,
      adminApiUrl: this.adminApiUrl,
    };

    const ref = this.dialog.open(SwitchUserModalComponent, config);
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result === 'refresh') {
        window.location.reload();
      }
    });
  }

  openCreateSettingsModal(): void {
    const titleMap: Record<SettingsTab, string> = {
      configSettings: 'Create new system setting',
      appconfigSettings: 'Create new app setting',
      clientconfigSettings: 'Create new client setting',
    };

    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '450px';
    config.disableClose = false;
    config.data = {
      title: titleMap[this.tab],
      settingType: this.tab,
      baseUrl: this.baseUrl,
      client: this.tab === 'appconfigSettings' ? this.selectedAppClient : this.selectedClient,
      appKey: this.tab === 'appconfigSettings' ? (this.selectedChannel?.channel_key ?? '') : '',
    };

    const ref = this.dialog.open(CreateSettingsModalComponent, config);
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result === 'saved') {
        this.refreshCurrentTab();
      }
    });
  }

  get createButtonLabel(): string {
    const labels: Record<SettingsTab, string> = {
      configSettings: 'Create New System Settings',
      appconfigSettings: 'Create New App Settings',
      clientconfigSettings: 'Create New Client Settings',
    };
    return labels[this.tab];
  }

  get visibleRows(): any[] {
    const rows = this.serverSettings[this.tab] || [];
    const searchTermMap: Record<SettingsTab, string> = {
      configSettings: this.searchConfig,
      appconfigSettings: this.searchAppConfig,
      clientconfigSettings: this.searchClientConfig,
    };
    const search = String(searchTermMap[this.tab] || '').toLowerCase().trim();
    if (!search) return rows;
    return rows.filter((r: any) => String(r.NAME || '').toLowerCase().includes(search));
  }

  get currentSearch(): string {
    const map: Record<SettingsTab, string> = {
      configSettings: this.searchConfig,
      appconfigSettings: this.searchAppConfig,
      clientconfigSettings: this.searchClientConfig,
    };
    return map[this.tab];
  }

  onSearchChange(value: string): void {
    if (this.tab === 'configSettings') this.searchConfig = value;
    else if (this.tab === 'appconfigSettings') this.searchAppConfig = value;
    else this.searchClientConfig = value;
    this.cdr.detectChanges();
  }

  trackByConfigKey(_: number, row: any): string {
    return String(row.CONFIG_KEY || row.NAME || _);
  }

  private decorateRows(rows: any[]): any[] {
    return (rows || []).map((row: any) => ({
      ...row,
      toggle: false,
      hover: false,
      enableActions: false,
      loading: false,
      loadingDeleteSettings: false,
    }));
  }

  private refreshCurrentTab(): void {
    if (this.tab === 'configSettings') {
      this.loadSettings();
    } else if (this.tab === 'appconfigSettings') {
      this.loadAppSettings();
    } else {
      this.loadClientSettings();
    }
  }
}
